import { useContext, useMemo } from 'react';
import {
  Episode,
  EpisodeAccessType,
  EpisodeReleaseStatus,
  LiveChannel,
  LiveChannelAccessType,
} from '../graphql/api.generated';
import { useIspSelector } from '../isp/ispHooks';
import { AuthContext, AuthMutatorContext } from './AuthContext';
import { PlayPermissionTypes } from './authEnums';
import type { AuthContext as AuthContextType } from './authInterfaces';

export const useAuth = () => useContext(AuthContext);
export const useAuthMutate = () => useContext(AuthMutatorContext);

export const playPermissionCheck = (
  auth: AuthContextType,
  ispName: string,
  episode?: Episode | null,
  channel?: LiveChannel | null,
  statusCode?: number,
  userOnlineScreeningDetails?: any,
) => {
  let hasPermission: boolean;
  let reason: PlayPermissionTypes;
  let ispLabel: string;

  if (!episode && !channel) return null;

  if (auth.user) {
    if (episode) {
      if (episode?.releaseStatus === EpisodeReleaseStatus.Released) {
        const isFree = episode.accessType === EpisodeAccessType.Free;
        if (isFree) {
          hasPermission = true;
          reason = PlayPermissionTypes.FREE_CONTENT;
        } else if (episode.accessType === EpisodeAccessType.SubReq) {
          if (auth.user.asp?.id) {
            hasPermission = true;
            reason = PlayPermissionTypes.SUBSCRIBED_USER;
          } else {
            hasPermission = false;
            reason = PlayPermissionTypes.SUBSCRIPTION_REQUIRED;
          }
        } else if (episode.accessType === EpisodeAccessType.OnlineScreening) {
          if (userOnlineScreeningDetails.loading) {
            hasPermission = false;
            reason = PlayPermissionTypes.LOADING;
          } else if (!!userOnlineScreeningDetails.data) {
            hasPermission = true;
            reason = PlayPermissionTypes.ONLINE_SCREENING_TICKET;
          } else {
            hasPermission = false;
            reason = PlayPermissionTypes.ONLINE_SCREENING_TICKET_REQUIRED;
          }
        } else if (episode.accessType === EpisodeAccessType.SubReqAndDataContract) {
          if (auth.user.asp?.id) {
            hasPermission = true;
            reason = PlayPermissionTypes.SUBSCRIBED_USER;
          } else {
            hasPermission = false;
            reason = PlayPermissionTypes.SUB_REQ_AND_DATA_CONTRACT;
            const findWhitelist = episode?.whitelistedIsps?.find(
              (item) => item.descriptor.toUpperCase() === ispName,
            );
            if (findWhitelist) {
              hasPermission = true;
              ispLabel = findWhitelist.name;
            }
          }
        }
      } else {
        hasPermission = false;
        reason = PlayPermissionTypes.CONTENT_NOT_READY;
      }
    }
    if (channel) {
      const isFree = channel.accessType === LiveChannelAccessType.Free;
      if (isFree) {
        hasPermission = true;
        reason = PlayPermissionTypes.FREE_CONTENT;
      } else {
        if (auth.user.asp?.id) {
          hasPermission = true;
          reason = PlayPermissionTypes.SUBSCRIBED_USER;
        } else {
          hasPermission = false;
          reason = PlayPermissionTypes.SUBSCRIPTION_REQUIRED;
        }
      }
    }
  } else {
    hasPermission = false;
    reason = PlayPermissionTypes.LOGIN_REQUIRED;
  }
  if (statusCode === 451) {
    hasPermission = false;
    reason = PlayPermissionTypes.IRAN_ACCESS;
  }
  return { hasPermission, reason, ispLabel };
};

export const usePlayPermissionCheck = (
  episode?: Episode | null,
  channel?: LiveChannel | null,
  statusCode?: number,
  userOnlineScreeningDetails?: any,
) => {
  const auth = useAuth();
  const ispName = useIspSelector();

  const result = useMemo(() => {
    return playPermissionCheck(
      auth,
      ispName!,
      episode,
      channel,
      statusCode,
      userOnlineScreeningDetails,
    );
  }, [episode, auth, ispName, userOnlineScreeningDetails]);

  return result;
};
